import { useCallback, useEffect, useRef, useState } from 'react'
import type { CurrentInsightSetup } from '@testfeed/shopify-contracts'
import type { InsightStudyConfirmation } from '../../hooks/study-builder/studyAuthoringClient'

export type InsightModalElement = HTMLElement & { showOverlay(): void; hideOverlay(): void }
type ReturnTarget = { trigger: HTMLElement; parent: InsightModalElement | null }
export type InsightConfirmationSession = {
  setup: CurrentInsightSetup
  confirmation: { current: InsightStudyConfirmation | null }
}

/** One details modal. Transfer out of the full-patterns modal, then restore its trigger on dismissal. */
export function useInsightOpportunityDetails() {
  const [session, setSession] = useState<InsightConfirmationSession | null>(null)
  const [open, setOpen] = useState(false)
  const returnTo = useRef<ReturnTarget | null>(null)
  const cancelTransition = useRef<(() => void) | null>(null)
  useEffect(
    () => () => {
      cancelTransition.current?.()
      returnTo.current = null
    },
    [],
  )

  const show = useCallback((chosen: CurrentInsightSetup, trigger: HTMLElement) => {
    if (returnTo.current) return
    const parent = trigger.closest('s-modal') as InsightModalElement | null
    returnTo.current = { trigger, parent }
    // A modal visit is a confirmation boundary. Closing discards it; a failed submission does not.
    setSession({ setup: chosen, confirmation: { current: null } })
    if (!parent) {
      setOpen(true)
      return
    }
    const afterHide = () => {
      cancelTransition.current = null
      setOpen(true)
    }
    parent.addEventListener('afterhide', afterHide, { once: true })
    cancelTransition.current = () => parent.removeEventListener('afterhide', afterHide)
    parent.hideOverlay()
  }, [])

  const closeAfterSave = useCallback(() => {
    returnTo.current = null
    setOpen(false)
  }, [])

  const afterHide = useCallback(() => {
    const target = returnTo.current
    returnTo.current = null
    setOpen(false)
    setSession(null)
    if (!target) return
    const focus = () => {
      if (target.trigger.isConnected) target.trigger.focus()
    }
    if (target.parent?.isConnected) {
      target.parent.addEventListener('aftershow', focus, { once: true })
      target.parent.showOverlay()
    } else focus()
  }, [])

  return { setup: session?.setup ?? null, session, open, show, closeAfterSave, afterHide }
}
